import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

// Import Redux actions
import { 
  fetchLivestreams, 
  fetchMyLivestreams, 
  setFilters, 
  setPagination, 
  resetFilters 
} from '../../store/LivestreamSlice';

// Import components
import LivestreamCard from './card/LivestreamCard';
import ScheduleLivestreamEvent from './ScheduleLivestreamEvent';
import UpdateLivestreamModal from './UpdateLivestreamModal';

// Import hooks
import useLivestreamPermissions from '../../hooks/useLivestreamPermissions';

// Import design system
import { componentClasses } from './utils/livestreamDesignSystem';

// Main LivestreamManagement Component
function LivestreamManagement() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { 
    livestreams, 
    loading, 
    error, 
    pagination, 
    filters 
  } = useSelector((state) => state.livestream);
  
  // Local state
  const [activeTab, setActiveTab] = useState('overview');
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedLivestream, setSelectedLivestream] = useState(null);
  const [viewMode, setViewMode] = useState('all'); // 'all' or 'mine'
  
  // Check permissions
  const canManage = user?.roles?.includes('SITE_OWNER') || user?.roles?.includes('ADMIN');
  
  // Fetch data
  const fetchData = useCallback(() => {
    console.log('🔍 LivestreamManagement - fetchData called:', { viewMode, canManage, pagination, filters });
    const fetchParams = {
      page: pagination.page,
      size: pagination.size,
      // Remove sort parameter to let backend handle default sorting
      ...filters
    };
    
    if (viewMode === 'mine' && canManage) {
      console.log('🔍 LivestreamManagement - Dispatching fetchMyLivestreams');
      dispatch(fetchMyLivestreams(fetchParams));
    } else {
      console.log('🔍 LivestreamManagement - Dispatching fetchLivestreams');
      dispatch(fetchLivestreams(fetchParams));
    }
  }, [dispatch, viewMode, canManage, pagination.page, pagination.size, filters]);
  
  // Debug logging for current state
  console.log('🔍 LivestreamManagement - Current State:', {
    loading,
    error,
    livestreamsCount: livestreams.length,
    pagination,
    filters,
    viewMode,
    canManage,
    user: user ? { id: user.id, roles: user.roles } : null
  });

  // Initial data fetch
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  // Handlers
  const handleTabChange = (tab) => {
    setActiveTab(tab);
  };
  
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    dispatch(setPagination({ page: 0 }));
  };
  
  const handleScheduleSuccess = () => {
    setShowScheduleModal(false);
    fetchData();
    toast.success('Livestream scheduled successfully!');
  };
  
  const handleUpdateSuccess = () => {
    setShowUpdateModal(false);
    setSelectedLivestream(null);
    fetchData();
    toast.success('Livestream updated successfully!');
  };
  
  const handleLivestreamClick = (livestream) => {
    setSelectedLivestream(livestream);
    setShowDetailsModal(true);
  };
  
  const handleEditLivestream = (livestream) => {
    setSelectedLivestream(livestream);
    setShowUpdateModal(true);
  };
  
  const handleWatchLivestream = (livestream) => {
    navigate(`/livestream/watch/${livestream.id}`, {
      state: { livestream }
    });
  };
  
  // Render overview tab
  const renderOverview = () => (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl sm:text-3xl font-bold text-title font-primary">
            {viewMode === 'mine' ? 'My Livestreams' : 'All Livestreams'}
          </h2>
          <p className="text-base sm:text-lg text-text-secondary font-secondary mt-1">
            {pagination.totalElements} stream{pagination.totalElements !== 1 ? 's' : ''} found
          </p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
          {canManage && (
            <div className="flex bg-white rounded-lg p-1 border border-border shadow-small w-full sm:w-auto">
              <button
                className={`flex-1 sm:flex-none px-4 py-2 rounded-lg font-secondary text-sm transition-all duration-300 ${
                  viewMode === 'all'
                    ? 'bg-accent-teal text-white shadow-small'
                    : 'bg-transparent text-text-secondary hover:bg-accent-teal/10'
                }`}
                onClick={() => handleViewModeChange('all')}
              >
                All Streams
              </button>
              <button
                className={`flex-1 sm:flex-none px-4 py-2 rounded-lg font-secondary text-sm transition-all duration-300 ${
                  viewMode === 'mine'
                    ? 'bg-accent-teal text-white shadow-small'
                    : 'bg-transparent text-text-secondary hover:bg-accent-teal/10'
                }`}
                onClick={() => handleViewModeChange('mine')}
              >
                My Streams
              </button>
            </div>
          )}
          
          {canManage && (
            <button
              className={componentClasses.buttonPrimary}
              onClick={() => setShowScheduleModal(true)}
            >
              ➕ Schedule
            </button>
          )}
        </div>
      </div>
      
      {/* Filters */}
      <div className={componentClasses.filterContainer}>
        <input
          type="text"
          placeholder="Search livestreams..."
          className={componentClasses.filterInput}
          value={filters.searchQuery || ''}
          onChange={(e) => dispatch(setFilters({ searchQuery: e.target.value }))}
        />
        
        <select
          className={componentClasses.filterSelect}
          value={filters.status || ''}
          onChange={(e) => dispatch(setFilters({ status: e.target.value || null }))}
        >
          <option value="">All Status</option>
          <option value="SCHEDULED">Scheduled</option>
          <option value="IN_PROGRESS">In Progress</option>
          <option value="COMPLETED">Completed</option>
          <option value="CANCELLED">Cancelled</option>
        </select>
        
        <button
          className={componentClasses.filterButton}
          onClick={() => dispatch(resetFilters())}
        >
          🗑️ Clear
        </button>
      </div>
      
      {/* Livestreams Grid */}
      {loading ? (
        <div className={componentClasses.loadingContainer}>
          <div className={componentClasses.loadingSpinner}></div>
        </div>
      ) : error ? (
        <div className={componentClasses.errorContainer}>
          <div className="text-4xl sm:text-5xl mb-4 opacity-50">⚠️</div>
          <h3 className="text-lg sm:text-xl font-bold text-text-primary mb-2 font-primary">Error Loading Livestreams</h3>
          <p className="text-sm sm:text-base text-text-secondary mb-4 font-secondary">
            {typeof error === 'string' ? error : error?.message || 'An error occurred'}
          </p>
          <button 
            className={componentClasses.errorButton}
            onClick={fetchData}
          >
            Try Again
          </button>
        </div>
      ) : livestreams.length === 0 ? (
        <div className={componentClasses.emptyContainer}>
          <div className="text-4xl sm:text-5xl mb-4 opacity-50">📺</div>
          <h3 className="text-lg sm:text-xl font-bold text-text-primary mb-2 font-primary">No Livestreams Found</h3>
          <p className="text-sm sm:text-base text-text-secondary mb-4 font-secondary">
            {viewMode === 'mine' 
              ? "You haven't scheduled any livestreams yet."
              : "No livestreams are currently available."
            }
          </p>
          {canManage && (
            <button
              className={componentClasses.emptyButton}
              onClick={() => setShowScheduleModal(true)}
            >
              ➕ Schedule Your First Stream
            </button>
          )}
        </div>
      ) : (
        <div className={componentClasses.livestreamGrid}>
          {livestreams.map((livestream) => (
            <LivestreamCard
              key={livestream.id}
              event={livestream}
              user={user}
              onClick={() => handleLivestreamClick(livestream)}
              onSuccess={fetchData}
            />
          ))}
        </div>
      )}
    </div>
  );
  
  // Render schedule tab
  const renderSchedule = () => (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-primary">Schedule New Livestream</h2>
        <button
          className="px-4 py-2 bg-transparent text-text-secondary border border-primary rounded-sm font-primary text-sm transition-all duration-300 hover:bg-accent-teal/10 hover:text-primary"
          onClick={() => setActiveTab('overview')}
        >
          ← Back to Overview
        </button>
      </div>
      
      <ScheduleLivestreamEvent onSuccess={handleScheduleSuccess} />
    </div>
  );
  
  // Render analytics tab
  const renderAnalytics = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-primary">Livestream Analytics</h2>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white border border-primary rounded-lg p-6">
          <h3 className="font-primary text-lg font-semibold text-title mb-2">Total Streams</h3>
          <p className="text-3xl font-bold text-accent-teal">{pagination.totalElements}</p>
        </div>
        <div className="bg-white border border-primary rounded-lg p-6">
          <h3 className="font-primary text-lg font-semibold text-title mb-2">Active Streams</h3>
          <p className="text-3xl font-bold text-accent-orange">
            {livestreams.filter(ls => ls.status === 'IN_PROGRESS').length}
          </p>
        </div>
        <div className="bg-white border border-primary rounded-lg p-6">
          <h3 className="font-primary text-lg font-semibold text-title mb-2">Scheduled</h3>
          <p className="text-3xl font-bold text-accent-grey">
            {livestreams.filter(ls => ls.status === 'SCHEDULED').length}
          </p>
        </div>
        <div className="bg-white border border-primary rounded-lg p-6">
          <h3 className="font-primary text-lg font-semibold text-title mb-2">Completed</h3>
          <p className="text-3xl font-bold text-success">
            {livestreams.filter(ls => ls.status === 'COMPLETED').length}
          </p>
        </div>
      </div>
    </div>
  );
  
  return (
    <div className={componentClasses.managementPage}>
      {/* Header */}
      <header className={componentClasses.managementHeader}>
        <div className={componentClasses.managementHeaderContent}>
          <div className={componentClasses.managementTitleSection}>
            <div className={componentClasses.managementIconContainer}>
              <span className="text-2xl">📺</span>
            </div>
            <div>
              <h1 className={componentClasses.managementTitle}>Livestream Management</h1>
              <p className={componentClasses.managementSubtitle}>
                Manage your livestreams, schedules, and streaming sessions
              </p>
            </div>
          </div>
          
          {/* Navigation Tabs */}
          <nav className={componentClasses.navContainer}>
            <button
              className={componentClasses.navTab(activeTab === 'overview')}
              onClick={() => handleTabChange('overview')}
            >
              Overview
            </button>
            {canManage && (
              <button
                className={componentClasses.navTab(activeTab === 'schedule')}
                onClick={() => handleTabChange('schedule')}
              >
                Schedule
              </button>
            )}
            <button
              className={componentClasses.navTab(activeTab === 'analytics')}
              onClick={() => handleTabChange('analytics')}
            >
              Analytics
            </button>
          </nav>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'schedule' && renderSchedule()}
        {activeTab === 'analytics' && renderAnalytics()}
      </main>
      
      {/* Modals */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-primary text-lg font-semibold text-title">Schedule New Livestream</h2>
              <button
                className="flex items-center justify-center w-8 h-8 bg-transparent text-text-secondary border-none rounded-sm cursor-pointer transition-all duration-300 hover:bg-accent-teal/10 hover:text-primary"
                onClick={() => setShowScheduleModal(false)}
              >
                ×
              </button>
            </div>
            <ScheduleLivestreamEvent onSuccess={handleScheduleSuccess} />
          </div>
        </div>
      )}
      
      {showUpdateModal && selectedLivestream && (
        <UpdateLivestreamModal
          show={showUpdateModal}
          onHide={() => {
            setShowUpdateModal(false);
            setSelectedLivestream(null);
          }}
          initialData={selectedLivestream}
          onUpdated={handleUpdateSuccess}
        />
      )}
      
      {showDetailsModal && selectedLivestream && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-primary text-lg font-semibold text-title">Livestream Details</h2>
              <button
                className="flex items-center justify-center w-8 h-8 bg-transparent text-text-secondary border-none rounded-sm cursor-pointer transition-all duration-300 hover:bg-accent-teal/10 hover:text-primary"
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedLivestream(null);
                }}
              >
                ×
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <h3 className="font-primary text-md font-semibold text-title mb-2">Stream Information</h3>
                <p className="font-secondary text-sm text-text-primary mb-2">
                  <strong>Title:</strong> {selectedLivestream.description || 'Untitled Livestream'}
                </p>
                <p className="font-secondary text-sm text-text-primary mb-2">
                  <strong>Status:</strong> 
                  <span className={`inline-block px-2 py-1 rounded-sm font-primary text-xs font-medium uppercase text-white ml-2 ${
                    selectedLivestream.status === 'IN_PROGRESS' ? 'bg-success' :
                    selectedLivestream.status === 'SCHEDULED' ? 'bg-warning' :
                    selectedLivestream.status === 'COMPLETED' ? 'bg-blue-500' :
                    selectedLivestream.status === 'CANCELLED' ? 'bg-error' : 'bg-gray-500'
                  }`}>
                    {selectedLivestream.status}
                  </span>
                </p>
                <p className="font-secondary text-sm text-text-primary mb-2">
                  <strong>Start Time:</strong> {new Date(selectedLivestream.startTime).toLocaleString()}
                </p>
                {selectedLivestream.endTime && (
                  <p className="font-secondary text-sm text-text-primary mb-2">
                    <strong>End Time:</strong> {new Date(selectedLivestream.endTime).toLocaleString()}
                  </p>
                )}
              </div>
              
              {selectedLivestream.court && (
                <div>
                  <h3 className="font-primary text-md font-semibold text-title mb-2">Court Information</h3>
                  <p className="font-secondary text-sm text-text-primary mb-2">
                    <strong>Court:</strong> {selectedLivestream.court.name}
                  </p>
                  <p className="font-secondary text-sm text-text-primary mb-2">
                    <strong>Location:</strong> {selectedLivestream.court.location}
                  </p>
                </div>
              )}
            </div>
            
            <div className="flex gap-2 justify-end mt-6">
              <button
                className="px-4 py-2 bg-transparent text-text-secondary border border-primary rounded-sm font-primary text-sm transition-all duration-300 hover:bg-accent-teal/10 hover:text-primary"
                onClick={() => {
                  setShowDetailsModal(false);
                  setSelectedLivestream(null);
                }}
              >
                Close
              </button>
              {canManage && (
                <button
                  className="px-4 py-2 bg-accent-teal text-white border border-accent-teal rounded-sm font-primary text-sm transition-all duration-300 hover:bg-accent-teal/90"
                  onClick={() => {
                    setShowDetailsModal(false);
                    handleEditLivestream(selectedLivestream);
                  }}
                >
                  Edit
                </button>
              )}
              <button
                className="px-4 py-2 bg-primary text-white border border-primary rounded-sm font-primary text-sm transition-all duration-300 hover:bg-opacity-90"
                onClick={() => {
                  setShowDetailsModal(false);
                  handleWatchLivestream(selectedLivestream);
                }}
              >
                Watch
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LivestreamManagement;
